import React, { useState, useEffect, useCallback } from 'react';
import {
  Tabs,
  Table,
  Tree,
  Button,
  Form,
  Input,
  Select,
  Space,
  Typography,
  Card,
  Row,
  Col,
  message,
  Tag,
  Divider,
  Modal,
  Switch,
  Popconfirm,
  Tooltip,
  Badge,
} from 'antd';
import {
  PlusOutlined,
  ReloadOutlined,
  EditOutlined,
  DeleteOutlined,
  FolderOutlined,
  FolderOpenOutlined,
  FileOutlined,
  SubnodeOutlined,
} from '@ant-design/icons';
import {
  getLookups,
  createVendor,
  createCategory,
  createUnit,
  listUsers,
  createUser,
  updateUser,
  deleteUser,
  updateVendor,
  deleteVendor,
  updateCategory,
  deleteCategory,
  updateUnit,
  deleteUnit,
  deleteMR,
  deletePO,
  updateDepartmentSettings,
} from '../../api';
import { useAuth } from '../../context/AuthContext';
import AdminPermissions from './AdminPermissions';
import CostCentersAdmin from './CostCentersAdmin';
import CategoryTreeSelect from '../../components/CategoryTreeSelect';
import ResponsiveTable from '../../components/responsive/ResponsiveTable';

// Admin tab → resource key mapping
const TAB_RESOURCE_MAP: Record<string, string> = {
  vendors: 'admin.vendors',
  categories: 'admin.categories',
  units: 'admin.units',
  users: 'admin.users',
  dataManagement: 'admin.data',
  permissions: 'admin.permissions',
  costCenters: 'admin.cost_centers',
  departments: 'admin.departments',
};

const { Title } = Typography;

interface Vendor {
  id: number;
  name: string;
  code?: string;
  contactInfo?: string;
}

interface CategoryNode {
  id: number;
  name: string;
  code?: string;
  description?: string;
  parentId?: number | null;
  level?: number;
  sortOrder?: number;
  isActive?: boolean;
  fullPath?: string;
  children?: CategoryNode[];
}

interface Unit {
  id: number;
  name: string;
  abbreviation?: string;
}

interface UserRecord {
  id: number;
  email: string;
  displayName: string;
  role: string;
  roles?: string[];
  departmentId: number;
  departmentCode?: string;
  extraDepartmentIds?: number[];
  isActive?: boolean;
}

interface Department {
  id: number;
  name: string;
  code?: string;
  requesterEntersPrice?: boolean;
}

const Admin: React.FC = () => {
  const { isAdmin, canAccess } = useAuth();
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [categories, setCategories] = useState<CategoryNode[]>([]);
  const [categoryTree, setCategoryTree] = useState<CategoryNode[]>([]);
  const [units, setUnits] = useState<Unit[]>([]);
  const [users, setUsers] = useState<UserRecord[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [loading, setLoading] = useState(true);

  const [vendorForm] = Form.useForm();
  const [categoryForm] = Form.useForm();
  const [unitForm] = Form.useForm();
  const [userForm] = Form.useForm();

  const [savingVendor, setSavingVendor] = useState(false);
  const [savingCategory, setSavingCategory] = useState(false);
  const [savingUnit, setSavingUnit] = useState(false);
  const [savingUser, setSavingUser] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [lookups, usersData] = await Promise.all([
        getLookups(),
        listUsers().catch(() => []),
      ]);
      setVendors(lookups.vendors || []);
      setCategories(lookups.categories || []);
      setCategoryTree(lookups.categoryTree || []);
      setUnits(lookups.units || []);
      setDepartments(lookups.departments || []);
      setUsers(Array.isArray(usersData) ? usersData : usersData?.data || []);
    } catch (err) {
      message.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const handleAddVendor = async (values: any) => {
    setSavingVendor(true);
    try {
      await createVendor(values);
      message.success('Vendor added');
      vendorForm.resetFields();
      loadAll();
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Failed to add vendor');
    } finally {
      setSavingVendor(false);
    }
  };

  const handleAddCategory = async (values: any) => {
    setSavingCategory(true);
    try {
      await createCategory(values);
      message.success('Category added');
      categoryForm.resetFields();
      loadAll();
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Failed to add category');
    } finally {
      setSavingCategory(false);
    }
  };

  const handleAddUnit = async (values: any) => {
    setSavingUnit(true);
    try {
      await createUnit(values);
      message.success('Unit added');
      unitForm.resetFields();
      loadAll();
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Failed to add unit');
    } finally {
      setSavingUnit(false);
    }
  };

  const handleAddUser = async (values: any) => {
    setSavingUser(true);
    try {
      await createUser(values);
      message.success('User created');
      userForm.resetFields();
      loadAll();
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Failed to create user');
    } finally {
      setSavingUser(false);
    }
  };

  // Edit User
  const [editUserModalOpen, setEditUserModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<UserRecord | null>(null);
  const [editUserForm] = Form.useForm();
  const [savingEditUser, setSavingEditUser] = useState(false);

  const handleEditUser = (record: UserRecord) => {
    setEditingUser(record);
    editUserForm.setFieldsValue({
      displayName: record.displayName,
      email: record.email,
      roles: record.roles?.length ? record.roles : [record.role],
      departmentId: record.departmentId,
      extraDepartmentIds: (record.extraDepartmentIds || []).filter((id) => id !== record.departmentId),
      isActive: record.isActive !== false,
      password: '',
    });
    setEditUserModalOpen(true);
  };

  const handleSaveEditUser = async () => {
    if (!editingUser) return;
    try {
      const values = await editUserForm.validateFields();
      setSavingEditUser(true);
      // Don't send empty password
      if (!values.password) delete values.password;
      values.extraDepartmentIds = (values.extraDepartmentIds || []).filter((id: number) => id !== values.departmentId);
      await updateUser(editingUser.id, values);
      message.success('User updated');
      setEditUserModalOpen(false);
      setEditingUser(null);
      loadAll();
    } catch (err: any) {
      if (err.response) message.error(err.response?.data?.error || 'Failed to update user');
    } finally {
      setSavingEditUser(false);
    }
  };

  // Edit Vendor
  const [editVendorModalOpen, setEditVendorModalOpen] = useState(false);
  const [editingVendor, setEditingVendor] = useState<Vendor | null>(null);
  const [editVendorForm] = Form.useForm();
  const [savingEditVendor, setSavingEditVendor] = useState(false);

  const handleEditVendor = (record: Vendor) => {
    setEditingVendor(record);
    editVendorForm.setFieldsValue({
      name: record.name,
      code: record.code || '',
      contactInfo: record.contactInfo || '',
    });
    setEditVendorModalOpen(true);
  };

  const handleSaveEditVendor = async () => {
    if (!editingVendor) return;
    try {
      const values = await editVendorForm.validateFields();
      setSavingEditVendor(true);
      await updateVendor(editingVendor.id, values);
      message.success('Vendor updated');
      setEditVendorModalOpen(false);
      setEditingVendor(null);
      loadAll();
    } catch (err: any) {
      if (err.response) message.error(err.response?.data?.error || 'Failed to update vendor');
    } finally {
      setSavingEditVendor(false);
    }
  };

  // Edit Unit
  const [editUnitModalOpen, setEditUnitModalOpen] = useState(false);
  const [editingUnit, setEditingUnit] = useState<Unit | null>(null);
  const [editUnitForm] = Form.useForm();
  const [savingEditUnit, setSavingEditUnit] = useState(false);

  const handleEditUnit = (record: Unit) => {
    setEditingUnit(record);
    editUnitForm.setFieldsValue({
      name: record.name,
      abbreviation: record.abbreviation || '',
    });
    setEditUnitModalOpen(true);
  };

  const handleSaveEditUnit = async () => {
    if (!editingUnit) return;
    try {
      const values = await editUnitForm.validateFields();
      setSavingEditUnit(true);
      await updateUnit(editingUnit.id, values);
      message.success('Unit updated');
      setEditUnitModalOpen(false);
      setEditingUnit(null);
      loadAll();
    } catch (err: any) {
      if (err.response) message.error(err.response?.data?.error || 'Failed to update unit');
    } finally {
      setSavingEditUnit(false);
    }
  };

  // Edit Category
  const [editCategoryModalOpen, setEditCategoryModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<CategoryNode | null>(null);
  const [editCategoryForm] = Form.useForm();
  const [savingEditCategory, setSavingEditCategory] = useState(false);

  // Add Subcategory (pre-fill parent)
  const [addCategoryModalOpen, setAddCategoryModalOpen] = useState(false);
  const [addCategoryParentId, setAddCategoryParentId] = useState<number | undefined>(undefined);
  const [addCategoryFormModal] = Form.useForm();
  const [savingAddCategoryModal, setSavingAddCategoryModal] = useState(false);

  const handleAddSubcategory = (parentNode: CategoryNode) => {
    setAddCategoryParentId(parentNode.id);
    addCategoryFormModal.setFieldsValue({
      name: '',
      code: '',
      description: '',
      parentId: parentNode.id,
    });
    setAddCategoryModalOpen(true);
  };

  const handleSaveAddCategoryModal = async () => {
    try {
      const values = await addCategoryFormModal.validateFields();
      setSavingAddCategoryModal(true);
      await createCategory(values);
      message.success('Category added');
      setAddCategoryModalOpen(false);
      addCategoryFormModal.resetFields();
      setAddCategoryParentId(undefined);
      loadAll();
    } catch (err: any) {
      if (err.response) message.error(err.response?.data?.error || 'Failed to add category');
    } finally {
      setSavingAddCategoryModal(false);
    }
  };

  const handleEditCategory = (record: CategoryNode) => {
    setEditingCategory(record);
    editCategoryForm.setFieldsValue({
      name: record.name,
      code: record.code || '',
      description: record.description || '',
      parentId: record.parentId || undefined,
      isActive: record.isActive !== false,
    });
    setEditCategoryModalOpen(true);
  };

  const handleSaveEditCategory = async () => {
    if (!editingCategory) return;
    try {
      const values = await editCategoryForm.validateFields();
      setSavingEditCategory(true);
      await updateCategory(editingCategory.id, values);
      message.success('Category updated');
      setEditCategoryModalOpen(false);
      setEditingCategory(null);
      loadAll();
    } catch (err: any) {
      if (err.response) message.error(err.response?.data?.error || 'Failed to update category');
    } finally {
      setSavingEditCategory(false);
    }
  };

  // Delete handlers for users, vendors, categories, units
  const handleDeleteUser = async (userId: number) => {
    try {
      await deleteUser(userId);
      message.success('User deactivated');
      loadAll();
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Failed to delete user');
    }
  };

  const handleDeleteVendor = async (vendorId: number) => {
    try {
      await deleteVendor(vendorId);
      message.success('Vendor deactivated');
      loadAll();
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Failed to delete vendor');
    }
  };

  const handleDeleteCategory = async (categoryId: number) => {
    try {
      await deleteCategory(categoryId);
      message.success('Category deleted');
      loadAll();
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Failed to delete category');
    }
  };

  const handleDeleteUnit = async (unitId: number) => {
    try {
      await deleteUnit(unitId);
      message.success('Unit deleted');
      loadAll();
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Failed to delete unit');
    }
  };

  // Delete MR/PO
  const [deleteMRId, setDeleteMRId] = useState<string>('');
  const [deletePOId, setDeletePOId] = useState<string>('');
  const [deletingMR, setDeletingMR] = useState(false);
  const [deletingPO, setDeletingPO] = useState(false);

  const handleDeleteMR = async () => {
    if (!deleteMRId.trim()) { message.warning('Enter a MR ID'); return; }
    setDeletingMR(true);
    try {
      await deleteMR(deleteMRId.trim());
      message.success(`MR ${deleteMRId} deleted`);
      setDeleteMRId('');
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Failed to delete MR');
    } finally {
      setDeletingMR(false);
    }
  };

  const handleDeletePO = async () => {
    if (!deletePOId.trim()) { message.warning('Enter a PO ID'); return; }
    setDeletingPO(true);
    try {
      await deletePO(deletePOId.trim());
      message.success(`PO ${deletePOId} deleted`);
      setDeletePOId('');
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Failed to delete PO');
    } finally {
      setDeletingPO(false);
    }
  };

  const roleColors: Record<string, string> = {
    admin: 'red',
    approver: 'gold',
    procurement_officer: 'blue',
    requester: 'green',
  };

  const hasAnyAdminAccess = isAdmin || canAccess('admin') ||
    Object.values(TAB_RESOURCE_MAP).some((r) => canAccess(r));

  if (!hasAnyAdminAccess) {
    return (
      <Card>
        <Title level={4}>Access Denied</Title>
        <p>You do not have permission to access the admin page.</p>
      </Card>
    );
  }

  // Filter tabs by permission
  const canAccessTab = (tabKey: string) =>
    isAdmin || canAccess(TAB_RESOURCE_MAP[tabKey] || '');

  const tabItems = [
    {
      key: 'vendors',
      label: 'Vendors',
      children: (
        <div>
          <Card size="small" title="Add New Vendor" style={{ marginBottom: 16 }}>
            <Form form={vendorForm} layout="inline" onFinish={handleAddVendor}>
              <Form.Item
                name="name"
                rules={[{ required: true, message: 'Name required' }]}
              >
                <Input placeholder="Vendor Name" style={{ width: 200 }} />
              </Form.Item>
              <Form.Item name="code">
                <Input placeholder="Code" style={{ width: 120 }} />
              </Form.Item>
              <Form.Item name="contactInfo">
                <Input placeholder="Contact Info" style={{ width: 200 }} />
              </Form.Item>
              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<PlusOutlined />}
                  loading={savingVendor}
                >
                  Add
                </Button>
              </Form.Item>
            </Form>
          </Card>
          <ResponsiveTable
            columns={[
              { title: 'ID', dataIndex: 'id', width: 60 },
              {
                title: 'Name',
                dataIndex: 'name',
                width: 250,
                render: (val: string, record: Vendor) => (
                  <Space>
                    {val}
                    {!record.code && <Tag color="orange">Auto-created</Tag>}
                  </Space>
                ),
              },
              { title: 'Code', dataIndex: 'code', width: 120, render: (v: string) => v || '-' },
              {
                title: 'Contact Info',
                dataIndex: 'contactInfo',
                ellipsis: true,
                render: (v: string) => v || '-',
              },
              {
                title: 'Action',
                width: 150,
                render: (_: any, record: Vendor) => (
                  <Space>
                    <Button size="small" icon={<EditOutlined />} onClick={() => handleEditVendor(record)}>
                      Edit
                    </Button>
                    <Popconfirm
                      title="Are you sure?"
                      description="This will deactivate the vendor."
                      onConfirm={() => handleDeleteVendor(record.id)}
                      okText="Yes"
                      okButtonProps={{ danger: true }}
                      cancelText="No"
                    >
                      <Button size="small" danger icon={<DeleteOutlined />}>
                        Delete
                      </Button>
                    </Popconfirm>
                  </Space>
                ),
              },
            ]}
            dataSource={vendors}
            card={{ header: ['name'] }}
            rowKey="id"
            size="small"
            loading={loading}
            pagination={{ pageSize: 15 }}
          />
        </div>
      ),
    },
    {
      key: 'categories',
      label: 'Categories',
      children: (() => {
        // Build Ant Design Tree data from categoryTree (hierarchical) or flat categories
        const buildAntTreeData = (nodes: CategoryNode[]): any[] =>
          nodes
            .filter((n) => n.isActive !== false)
            .map((node) => ({
              key: node.id,
              title: (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontWeight: node.level === 0 ? 600 : 400 }}>
                    {node.name}
                  </span>
                  {node.code && (
                    <Tag color="blue" style={{ fontSize: 10, lineHeight: '16px', margin: 0 }}>
                      {node.code}
                    </Tag>
                  )}
                  {node.isActive === false && (
                    <Tag color="red" style={{ fontSize: 10, lineHeight: '16px', margin: 0 }}>
                      Inactive
                    </Tag>
                  )}
                  <Space size={2} style={{ marginLeft: 8 }}>
                    <Tooltip title="Add Subcategory">
                      <Button
                        type="text"
                        size="small"
                        icon={<SubnodeOutlined />}
                        onClick={(e) => { e.stopPropagation(); handleAddSubcategory(node); }}
                        style={{ fontSize: 12, color: '#1677ff' }}
                      />
                    </Tooltip>
                    <Tooltip title="Edit">
                      <Button
                        type="text"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={(e) => { e.stopPropagation(); handleEditCategory(node); }}
                        style={{ fontSize: 12 }}
                      />
                    </Tooltip>
                    <Popconfirm
                      title="Deactivate this category?"
                      description={node.children?.length ? 'Remove subcategories first.' : 'This will deactivate the category.'}
                      onConfirm={() => handleDeleteCategory(node.id)}
                      okText="Yes"
                      okButtonProps={{ danger: true }}
                      cancelText="No"
                      disabled={!!node.children?.length}
                    >
                      <Tooltip title={node.children?.length ? 'Has subcategories' : 'Deactivate'}>
                        <Button
                          type="text"
                          size="small"
                          danger
                          icon={<DeleteOutlined />}
                          disabled={!!node.children?.length}
                          onClick={(e) => e.stopPropagation()}
                          style={{ fontSize: 12 }}
                        />
                      </Tooltip>
                    </Popconfirm>
                  </Space>
                </span>
              ),
              icon: node.children?.length ? <FolderOutlined /> : <FileOutlined />,
              children: node.children?.length ? buildAntTreeData(node.children) : undefined,
              _raw: node,
            }));

        const treeNodes = categoryTree.length > 0 ? buildAntTreeData(categoryTree) : [];
        const inactiveCategories = categories.filter((c) => c.isActive === false);

        return (
          <div>
            <Card size="small" title="Add Top-Level Category" style={{ marginBottom: 16 }}>
              <Form form={categoryForm} layout="inline" onFinish={handleAddCategory}>
                <Form.Item
                  name="name"
                  rules={[{ required: true, message: 'Name required' }]}
                >
                  <Input placeholder="Category Name" style={{ width: 180 }} />
                </Form.Item>
                <Form.Item name="code">
                  <Input placeholder="Code (e.g. FAB)" style={{ width: 130 }} />
                </Form.Item>
                <Form.Item name="description">
                  <Input placeholder="Description" style={{ width: 200 }} />
                </Form.Item>
                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    icon={<PlusOutlined />}
                    loading={savingCategory}
                  >
                    Add
                  </Button>
                </Form.Item>
              </Form>
            </Card>

            {treeNodes.length > 0 ? (
              <Card
                size="small"
                title={
                  <Space>
                    <FolderOpenOutlined />
                    <span>Category Hierarchy</span>
                    <Badge count={categories.filter((c) => c.isActive !== false).length} style={{ backgroundColor: '#1677ff' }} overflowCount={999} />
                  </Space>
                }
              >
                <Tree
                  showIcon
                  defaultExpandAll
                  treeData={treeNodes}
                  selectable={false}
                  style={{ fontSize: 13 }}
                />
              </Card>
            ) : (
              <ResponsiveTable
                columns={[
                  { title: 'ID', dataIndex: 'id', width: 60 },
                  { title: 'Name', dataIndex: 'name', width: 250 },
                  { title: 'Code', dataIndex: 'code', width: 120, render: (v: string) => v || '-' },
                  {
                    title: 'Action',
                    width: 150,
                    render: (_: any, record: CategoryNode) => (
                      <Space>
                        <Button size="small" icon={<EditOutlined />} onClick={() => handleEditCategory(record)}>
                          Edit
                        </Button>
                        <Popconfirm
                          title="Are you sure?"
                          description="This will deactivate the category."
                          onConfirm={() => handleDeleteCategory(record.id)}
                          okText="Yes"
                          okButtonProps={{ danger: true }}
                          cancelText="No"
                        >
                          <Button size="small" danger icon={<DeleteOutlined />}>
                            Delete
                          </Button>
                        </Popconfirm>
                      </Space>
                    ),
                  },
                ]}
                dataSource={categories}
                card={{ header: ['name'] }}
                rowKey="id"
                size="small"
                loading={loading}
                pagination={{ pageSize: 15 }}
              />
            )}

            {inactiveCategories.length > 0 && (
              <Card size="small" title="Inactive Categories" style={{ marginTop: 16 }}>
                <ResponsiveTable
                  columns={[
                    { title: 'ID', dataIndex: 'id', width: 60 },
                    { title: 'Name', dataIndex: 'fullPath', render: (_: any, r: CategoryNode) => r.fullPath || r.name },
                    { title: 'Code', dataIndex: 'code', width: 100, render: (v: string) => v || '-' },
                    {
                      title: 'Action',
                      width: 120,
                      render: (_: any, record: CategoryNode) => (
                        <Button
                          size="small"
                          onClick={async () => {
                            try {
                              await updateCategory(record.id, { isActive: true });
                              message.success('Category reactivated');
                              loadAll();
                            } catch (err: any) {
                              message.error(err.response?.data?.error || 'Failed');
                            }
                          }}
                        >
                          Reactivate
                        </Button>
                      ),
                    },
                  ]}
                  dataSource={inactiveCategories}
                  card={{ header: ['fullPath'] }}
                  rowKey="id"
                  size="small"
                  pagination={false}
                />
              </Card>
            )}
          </div>
        );
      })(),
    },
    {
      key: 'units',
      label: 'Units',
      children: (
        <div>
          <Card size="small" title="Add New Unit" style={{ marginBottom: 16 }}>
            <Form form={unitForm} layout="inline" onFinish={handleAddUnit}>
              <Form.Item
                name="name"
                rules={[{ required: true, message: 'Name required' }]}
              >
                <Input placeholder="Unit Name" style={{ width: 200 }} />
              </Form.Item>
              <Form.Item name="abbreviation">
                <Input placeholder="Abbreviation" style={{ width: 120 }} />
              </Form.Item>
              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<PlusOutlined />}
                  loading={savingUnit}
                >
                  Add
                </Button>
              </Form.Item>
            </Form>
          </Card>
          <ResponsiveTable
            columns={[
              { title: 'ID', dataIndex: 'id', width: 60 },
              { title: 'Name', dataIndex: 'name', width: 250 },
              {
                title: 'Abbreviation',
                dataIndex: 'abbreviation',
                width: 120,
                render: (v: string) => v || '-',
              },
              {
                title: 'Action',
                width: 150,
                render: (_: any, record: Unit) => (
                  <Space>
                    <Button size="small" icon={<EditOutlined />} onClick={() => handleEditUnit(record)}>
                      Edit
                    </Button>
                    <Popconfirm
                      title="Are you sure?"
                      description="This will delete the unit."
                      onConfirm={() => handleDeleteUnit(record.id)}
                      okText="Yes"
                      okButtonProps={{ danger: true }}
                      cancelText="No"
                    >
                      <Button size="small" danger icon={<DeleteOutlined />}>
                        Delete
                      </Button>
                    </Popconfirm>
                  </Space>
                ),
              },
            ]}
            dataSource={units}
            card={{ header: ['name'] }}
            rowKey="id"
            size="small"
            loading={loading}
            pagination={{ pageSize: 15 }}
          />
        </div>
      ),
    },
    {
      key: 'departments',
      label: 'Departments',
      children: (
        <ResponsiveTable
          columns={[
            { title: 'Code', dataIndex: 'code', width: 90 },
            { title: 'Name', dataIndex: 'name', width: 240 },
            {
              title: 'Requesters enter an estimated price',
              key: 'requesterEntersPrice',
              render: (_: any, d: Department) => (
                <Space>
                  <Switch
                    checked={!!d.requesterEntersPrice}
                    onChange={async (on) => {
                      try {
                        await updateDepartmentSettings(d.id, { requesterEntersPrice: on });
                        setDepartments((prev) => prev.map((x) => (x.id === d.id ? { ...x, requesterEntersPrice: on } : x)));
                        message.success(`${d.name}: estimated price ${on ? 'enabled' : 'disabled'} for requesters`);
                      } catch (e: any) {
                        message.error(e.response?.data?.error || 'Could not update department');
                      }
                    }}
                  />
                  <span style={{ color: '#888', fontSize: 12 }}>{d.requesterEntersPrice ? "Shown on the MR line as the requester's estimate" : 'Requesters see no price column'}</span>
                </Space>
              ),
            },
          ]}
          dataSource={departments}
          card={{ header: ['name', 'code'] }}
          rowKey="id"
          size="small"
          loading={loading}
          pagination={false}
        />
      ),
    },
    {
      key: 'users',
      label: 'Users',
      children: (
        <div>
          <Card size="small" title="Create New User" style={{ marginBottom: 16 }}>
            <Form form={userForm} onFinish={handleAddUser} layout="inline">
              <Form.Item
                name="displayName"
                rules={[{ required: true, message: 'Name required' }]}
              >
                <Input placeholder="Display Name" style={{ width: 180 }} />
              </Form.Item>
              <Form.Item
                name="email"
                rules={[
                  { required: true, message: 'Email required' },
                  { type: 'email', message: 'Valid email required' },
                ]}
              >
                <Input placeholder="Email" style={{ width: 200 }} />
              </Form.Item>
              <Form.Item
                name="password"
                rules={[{ required: true, message: 'Password required' }]}
              >
                <Input.Password placeholder="Password" style={{ width: 150 }} />
              </Form.Item>
              <Form.Item
                name="roles"
                rules={[{ required: true, message: 'Role required' }]}
              >
                <Select
                  mode="multiple"
                  placeholder="Select roles"
                  style={{ width: 240 }}
                  options={[
                    { label: 'Admin', value: 'admin' },
                    { label: 'Approver (CEO)', value: 'approver' },
                    { label: 'Procurement Officer', value: 'procurement_officer' },
                    { label: 'Requester', value: 'requester' },
                  ]}
                />
              </Form.Item>
              <Form.Item
                name="departmentId"
                rules={[{ required: true, message: 'Department required' }]}
              >
                <Select
                  placeholder="Department"
                  style={{ width: 180 }}
                  options={departments.map((d) => ({ label: d.name, value: d.id }))}
                  showSearch
                  filterOption={(input, option) =>
                    (option?.label as string || '').toLowerCase().includes(input.toLowerCase())
                  }
                />
              </Form.Item>
              <Form.Item name="extraDepartmentIds">
                <Select
                  mode="multiple"
                  placeholder="Also sees departments"
                  style={{ width: 220 }}
                  maxTagCount="responsive"
                  optionFilterProp="label"
                  options={departments.map((d) => ({ label: d.name, value: d.id }))}
                />
              </Form.Item>
              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<PlusOutlined />}
                  loading={savingUser}
                >
                  Create
                </Button>
              </Form.Item>
            </Form>
          </Card>
          <ResponsiveTable
            columns={[
              { title: 'ID', dataIndex: 'id', width: 60 },
              { title: 'Name', dataIndex: 'displayName', width: 180 },
              { title: 'Email', dataIndex: 'email', width: 220 },
              {
                title: 'Roles',
                dataIndex: 'roles',
                key: 'roles',
                width: 200,
                render: (_: any, record: UserRecord) => {
                  const roles = record.roles?.length ? record.roles : [record.role];
                  return (
                    <Space size={[0, 4]} wrap>
                      {roles.map((r: string) => (
                        <Tag key={r} color={roleColors[r] || 'default'} style={{ fontSize: 11 }}>
                          {r.replace(/_/g, ' ').toUpperCase()}
                        </Tag>
                      ))}
                    </Space>
                  );
                },
              },
              {
                title: 'Department',
                key: 'department',
                width: 160,
                render: (_: any, record: UserRecord) => {
                  const dept = departments.find(d => d.id === record.departmentId);
                  return dept?.name || record.departmentCode || '-';
                },
              },
              {
                title: 'Also sees',
                key: 'extraDepartments',
                width: 200,
                render: (_: any, record: UserRecord) => {
                  const extras = (record.extraDepartmentIds || []).filter((id) => id !== record.departmentId);
                  if (!extras.length) return <span style={{ color: '#999' }}>—</span>;
                  return (
                    <Space size={[0, 4]} wrap>
                      {extras.map((id) => (
                        <Tag key={id} color="blue" style={{ fontSize: 11 }}>{departments.find((d) => d.id === id)?.name || `#${id}`}</Tag>
                      ))}
                    </Space>
                  );
                },
              },
              {
                title: 'Active',
                dataIndex: 'isActive',
                width: 80,
                render: (val: boolean) =>
                  val !== false ? <Tag color="green">Yes</Tag> : <Tag color="red">No</Tag>,
              },
              {
                title: 'Action',
                width: 150,
                render: (_: any, record: UserRecord) => (
                  <Space>
                    <Button size="small" icon={<EditOutlined />} onClick={() => handleEditUser(record)}>
                      Edit
                    </Button>
                    <Popconfirm
                      title="Are you sure?"
                      description="This will deactivate the user."
                      onConfirm={() => handleDeleteUser(record.id)}
                      okText="Yes"
                      okButtonProps={{ danger: true }}
                      cancelText="No"
                    >
                      <Button size="small" danger icon={<DeleteOutlined />}>
                        Delete
                      </Button>
                    </Popconfirm>
                  </Space>
                ),
              },
            ]}
            dataSource={users}
            card={{ header: ['displayName'] }}
            rowKey="id"
            size="small"
            loading={loading}
            pagination={{ pageSize: 15 }}
          />
        </div>
      ),
    },
    {
      key: 'dataManagement',
      label: 'Data Management',
      children: (
        <div>
          <Card size="small" title="Delete Material Request" style={{ marginBottom: 16 }}>
            <Space>
              <Input
                placeholder="Enter MR ID (e.g. 5 or MR-0005)"
                value={deleteMRId}
                onChange={(e) => setDeleteMRId(e.target.value)}
                style={{ width: 280 }}
              />
              <Popconfirm
                title="Are you sure you want to delete this MR?"
                description="This action cannot be undone."
                onConfirm={handleDeleteMR}
                okText="Yes, Delete"
                okButtonProps={{ danger: true }}
                cancelText="Cancel"
              >
                <Button danger icon={<DeleteOutlined />} loading={deletingMR}>
                  Delete MR
                </Button>
              </Popconfirm>
            </Space>
          </Card>
          <Card size="small" title="Delete Purchase Order">
            <Space>
              <Input
                placeholder="Enter PO ID (e.g. 3 or PO-0003)"
                value={deletePOId}
                onChange={(e) => setDeletePOId(e.target.value)}
                style={{ width: 280 }}
              />
              <Popconfirm
                title="Are you sure you want to delete this PO?"
                description="This action cannot be undone."
                onConfirm={handleDeletePO}
                okText="Yes, Delete"
                okButtonProps={{ danger: true }}
                cancelText="Cancel"
              >
                <Button danger icon={<DeleteOutlined />} loading={deletingPO}>
                  Delete PO
                </Button>
              </Popconfirm>
            </Space>
          </Card>
        </div>
      ),
    },
    {
      key: 'costCenters',
      label: 'Cost Centers',
      children: <CostCentersAdmin />,
    },
    {
      key: 'permissions',
      label: 'Permissions',
      children: <AdminPermissions />,
    },
  ];

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={4} style={{ margin: 0 }}>
            Administration
          </Title>
        </Col>
        <Col>
          <Button icon={<ReloadOutlined />} onClick={loadAll}>
            Refresh
          </Button>
        </Col>
      </Row>

      <Tabs items={tabItems.filter((t) => canAccessTab(t.key))} />

      <Modal
        title="Edit User"
        open={editUserModalOpen}
        onOk={handleSaveEditUser}
        onCancel={() => { setEditUserModalOpen(false); setEditingUser(null); }}
        confirmLoading={savingEditUser}
        okText="Save"
      >
        <Form form={editUserForm} layout="vertical">
          <Form.Item name="displayName" label="Display Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="email" label="Email" rules={[{ required: true }, { type: 'email' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="roles" label="Roles" rules={[{ required: true }]}>
            <Select
              mode="multiple"
              options={[
                { label: 'Admin', value: 'admin' },
                { label: 'Approver (CEO)', value: 'approver' },
                { label: 'Procurement Officer', value: 'procurement_officer' },
                { label: 'Requester', value: 'requester' },
              ]}
            />
          </Form.Item>
          <Form.Item name="departmentId" label="Department" rules={[{ required: true }]}>
            <Select
              options={departments.map((d) => ({ label: d.name, value: d.id }))}
              showSearch
              filterOption={(input, option) =>
                (option?.label as string || '').toLowerCase().includes(input.toLowerCase())
              }
            />
          </Form.Item>
          <Form.Item
            name="extraDepartmentIds"
            label="Additional departments"
            extra="The user can view and create MRs and POs for their home department plus every department listed here. Leave empty for a normal single-department user. Takes effect at their next login."
          >
            <Select
              mode="multiple"
              allowClear
              placeholder="None"
              maxTagCount="responsive"
              optionFilterProp="label"
              options={departments.map((d) => ({ label: d.name, value: d.id }))}
            />
          </Form.Item>
          <Form.Item name="isActive" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="password" label="New Password (leave empty to keep current)">
            <Input.Password placeholder="Enter new password" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Edit Vendor"
        open={editVendorModalOpen}
        onOk={handleSaveEditVendor}
        onCancel={() => { setEditVendorModalOpen(false); setEditingVendor(null); }}
        confirmLoading={savingEditVendor}
        okText="Save"
      >
        <Form form={editVendorForm} layout="vertical">
          <Form.Item name="name" label="Vendor Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="code" label="Code">
            <Input />
          </Form.Item>
          <Form.Item name="contactInfo" label="Contact Info">
            <Input />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Edit Unit"
        open={editUnitModalOpen}
        onOk={handleSaveEditUnit}
        onCancel={() => { setEditUnitModalOpen(false); setEditingUnit(null); }}
        confirmLoading={savingEditUnit}
        okText="Save"
      >
        <Form form={editUnitForm} layout="vertical">
          <Form.Item name="name" label="Unit Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="abbreviation" label="Abbreviation">
            <Input />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Edit Category"
        open={editCategoryModalOpen}
        onOk={handleSaveEditCategory}
        onCancel={() => { setEditCategoryModalOpen(false); setEditingCategory(null); }}
        confirmLoading={savingEditCategory}
        okText="Save"
      >
        <Form form={editCategoryForm} layout="vertical">
          <Form.Item name="name" label="Category Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="code" label="Code">
            <Input placeholder="e.g. FAB, TRM" />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="parentId" label="Parent Category">
            <CategoryTreeSelect
              categoryTree={categoryTree}
              categories={categories}
              allowClear
              placeholder="None (top-level)"
            />
          </Form.Item>
          <Form.Item name="isActive" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={addCategoryParentId ? 'Add Subcategory' : 'Add Category'}
        open={addCategoryModalOpen}
        onOk={handleSaveAddCategoryModal}
        onCancel={() => { setAddCategoryModalOpen(false); addCategoryFormModal.resetFields(); setAddCategoryParentId(undefined); }}
        confirmLoading={savingAddCategoryModal}
        okText="Create"
      >
        <Form form={addCategoryFormModal} layout="vertical">
          <Form.Item name="parentId" label="Parent Category">
            <CategoryTreeSelect
              categoryTree={categoryTree}
              categories={categories}
              allowClear
              placeholder="None (top-level)"
            />
          </Form.Item>
          <Form.Item name="name" label="Category Name" rules={[{ required: true, message: 'Name is required' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="code" label="Code">
            <Input placeholder="e.g. SLK, BTN" />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Admin;
